// How the two "effectiveness" numbers are presented to the player.
//
// Both backends ship raw numbers that only make sense to the scorer:
//   * examine  — `found_this_round / still_unfound_at_round_start * 100`
//                (0-100, one decimal), a PER-ROUND ratio; a round with nothing
//                left to find stores 0 with `scored: false` and the scorer
//                leaves it out (A4-F6);
//   * interrogation — a 0.0-1.0 float from the approach x personality table.
//
// A bare percent reads as a grade the player cannot act on. These helpers turn
// both into something that answers a question the player is actually asking:
// "have I cleared this room?" and "was that the right way to work this
// suspect?". They are pure so every renderer of either number agrees.

/**
 * One color system for both scales, so a coverage badge and an approach label
 * never disagree about what "good" looks like. Tailwind classes, not hex:
 * these are new surfaces and nothing here is theme-specific.
 */
export const TONE_CLASSES = {
  neutral: { text: 'text-slate-400', bg: 'bg-slate-400/10', border: 'border-l-slate-400' },
  negative: { text: 'text-red-400', bg: 'bg-red-400/10', border: 'border-l-red-400' },
  caution: { text: 'text-yellow-400', bg: 'bg-yellow-400/10', border: 'border-l-yellow-400' },
  positive: { text: 'text-green-400', bg: 'bg-green-400/10', border: 'border-l-green-400' },
};

/** An examination record's `clues_found` entry is an id; tolerate `{id}` too. */
const clueIdOf = (entry) => {
  if (typeof entry === 'string') return entry || null;
  if (entry && typeof entry === 'object' && typeof entry.id === 'string') return entry.id || null;
  return null;
};

const collectIds = (into, entries) => {
  (Array.isArray(entries) ? entries : []).forEach((entry) => {
    const id = clueIdOf(entry);
    if (id) into.add(id);
  });
  return into;
};

/**
 * Cumulative examine coverage for one location.
 *
 * The numerator is every clue on this location's examine-findable roster that
 * the player HOLDS, by whatever route — not just the ones examine itself
 * turned up. A clue can reach the case file another way (the cipher reward
 * sits at a location; an interrogation can hand one over), and counting only
 * `clues_found` left such a location reading "0 of 1 · 0%" forever even after
 * the player held the clue (SF round 4).
 *
 * The count comes from the backend (`clues_known_at_location`, derived at
 * serialization time by `helpers/examination_coverage.py`), not from the clues
 * the client holds: the client cannot tell an examine-findable clue from an
 * interrogation-only one, because `revealed_by` is not in
 * `PUBLIC_CLUE_FIELDS`, so counting held clues by `location` here would let an
 * interrogation-only clue inflate the numerator into a false "cleared". A
 * count rather than ids also keeps undiscovered clue ids off the wire. Records
 * without it (the tutorial, which has its own display anyway) fall back to
 * `clues_found` alone.
 *
 * @param {object} args
 * @param {Array<object>} args.rounds - `examinations[locationId]`; each record
 *   carries `clues_found` and `total_clues_at_location`. Pro-gen ships a bare
 *   list and featured-compat maps `{rounds_completed, rounds}` onto the same
 *   shape (`compat._examinations`), so one argument covers both games. Both
 *   backends append the round just completed BEFORE building `updatedState`
 *   (`routes/game.py:4491` then `:5021`; `featured_cases/engine.py:408` then
 *   `compat.build_updated_state`), so a caller rendering a fresh examination
 *   passes the response's rounds and needs no separate "current round" input.
 * @param {number} [args.total] - denominator override; defaults to the largest
 *   `total_clues_at_location` seen across the rounds (constant per location,
 *   since the clue roster is static).
 * @returns {{found: number, total: number, percent: number, tone: string}|null}
 *   `null` when there is nothing meaningful to render (no examination rounds,
 *   or a missing/zero total), so callers render nothing rather than `NaN%` or
 *   a count for a location the player has never examined.
 */
export const examineCoverage = ({ rounds, total } = {}) => {
  const records = Array.isArray(rounds) ? rounds : [];

  // No examination record means no reading at all. This has to precede the
  // denominator: a caller that passes a `total` for a location the player has
  // never examined would otherwise read "0 of N", leaking how many clues are
  // findable in a room nobody has entered.
  if (records.length === 0) return null;

  const foundHere = new Set();
  records.forEach((record) => collectIds(foundHere, record?.clues_found));

  // Clues on this location's roster that the player holds, however they got
  // them. Every payload re-stamps every round of every location with the same
  // current count, and every client write replaces the whole `examinations`
  // map, so the value is identical across a location's rounds. `max` is only
  // tolerance for a record written without the field (tutorial, or a payload
  // from before the field existed).
  const knownHere = records.reduce(
    (best, record) => Math.max(best, Number(record?.clues_known_at_location) || 0),
    0
  );

  const declaredTotal = total === undefined || total === null
    ? records.reduce(
      (best, record) => Math.max(best, Number(record?.total_clues_at_location) || 0),
      0
    )
    : Number(total);

  if (!Number.isFinite(declaredTotal) || declaredTotal <= 0) return null;

  // Numerator and denominator draw on the same population, so the clamp
  // should never bind; it exists so a backend that ever shrinks the
  // denominator cannot make the UI print "3 of 2".
  const found = Math.min(Math.max(foundHere.size, knownHere), declaredTotal);
  const percent = Math.round((found / declaredTotal) * 100);

  return {
    found,
    total: declaredTotal,
    percent,
    tone: found === 0 ? 'neutral'
      : percent >= 100 ? 'positive'
        : percent >= 50 ? 'caution' : 'negative',
  };
};

/** The one-line label for a coverage result, e.g. "Clues found here: 2 of 3 · 67%". */
export const formatExamineCoverage = (coverage) => (
  coverage
    ? `Clues found here: ${coverage.found} of ${coverage.total} · ${coverage.percent}%`
    : null
);

/** Shared copy for a location the backend says has nothing to examine. */
export const NO_EVIDENCE_AT_LOCATION = 'No evidence available at this location';

/**
 * Whether the player has evidence from this location that the clue-only
 * coverage figure cannot count, so a `total_clues_at_location` of 0 must not be
 * read out as NO_EVIDENCE_AT_LOCATION. True when either:
 *   * a round here recorded a document (`documents_found`, featured only), or
 *   * a HELD document's `location` is this location, however it was obtained
 *     (e.g. handed over in an interview), matching how the clue counter credits
 *     clues by any route.
 * Callers render no counter in that case; there is no documents denominator.
 *
 * @param {object} args
 * @param {string} args.locationId
 * @param {Array<object>} [args.rounds] - `examinations[locationId]`.
 * @param {Array<object>} [args.documents] - the store's document objects.
 * @param {Array<string>} [args.discoveredDocumentIds] - `discovered.documents`.
 * @returns {boolean}
 */
export const hasNonClueEvidenceAtLocation = ({
  locationId,
  rounds,
  documents,
  discoveredDocumentIds,
} = {}) => {
  const records = Array.isArray(rounds) ? rounds : [];
  if (records.some(
    (record) => Array.isArray(record?.documents_found) && record.documents_found.length > 0
  )) return true;

  if (!locationId || !Array.isArray(documents) || !Array.isArray(discoveredDocumentIds)) {
    return false;
  }
  const held = new Set(discoveredDocumentIds);
  return documents.some((doc) => Boolean(doc) && held.has(doc.id) && doc.location === locationId);
};

// Matches the backend reveal-tier cut (>= 0.4 is MEDIUM), so a cell labelled Poor is never one that still reveals at MEDIUM.
const POOR_BELOW = 0.4;
const FAIR_BELOW = 0.7;

/** Caption that says what the approach label is measuring. */
export const APPROACH_FIT_CAPTION = 'How well your approach suited this suspect.';

/**
 * Turn a 0.0-1.0 interrogation effectiveness into a label the player can read.
 *
 * @param {number|null|undefined} value
 * @returns {{key: 'poor'|'fair'|'strong', label: string, tone: string}|null}
 *   `null` for a missing or non-numeric value, so the caller renders nothing.
 */
export const approachFit = (value) => {
  if (value === null || value === undefined || value === '' || typeof value === 'boolean') {
    return null;
  }
  const fit = Number(value);
  if (!Number.isFinite(fit)) return null;

  if (fit < POOR_BELOW) return { key: 'poor', label: 'Poor approach', tone: 'negative' };
  if (fit < FAIR_BELOW) return { key: 'fair', label: 'Fair approach', tone: 'caution' };
  return { key: 'strong', label: 'Strong approach', tone: 'positive' };
};

/**
 * The same label for the score breakdown, where the backend ships the average
 * as a 0-100 integer instead of a 0.0-1.0 float.
 */
export const approachFitFromPercent = (percent) => (
  percent === null || percent === undefined || percent === '' || typeof percent === 'boolean'
    ? null
    : approachFit(Number(percent) / 100)
);
